// Imports
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { EMPLOYEE_WEEK_OFF } from "../../constants/api";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { Calendar, Edit } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import { configBranchLabel } from "../../utils/branchSession";

const EmployeeWeekOff = () => {
    // States
    const [EmployeeWeekOffData, setEmployeeWeekOffData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const navigate = useNavigate();

    // Permissions
    const {
        add: addPermission,
        edit: editPermission,
        draft: draftPermission,
        active: isActive
    } = checkUserPermissionFunc("configuration", "Week off", "SubMenu");

    // Functions
    const handleAddWeekOff = () => {
        navigate("/configuration/weekoff/add", {
            state: { fromEdit: false, editDetails: {}, isActive: isActive }
        });
    };

    const handleEditWeekOff = (row) => {
        navigate("/configuration/weekoff/add", {
            state: {
                fromEdit: true,
                editDetails: row,
                isActive: isActive
            }
        });
    };

    // Refresh table data
    const refreshTable = () => {
        setServerTrigger(prev => !prev);
    };

    // Columns
    const EmployeeWeekOffColumns = [
        {
            accessor: "code",
            Header: "Week Off Code",
            sortable: true,
            filterable: true,
            width: '120px',
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "branch_name",
            Header: "Branch",
            sortable: true,
            filterable: true,
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "weekoff_name",
            Header: "Week Off Name",
            sortable: true,
            filterable: true,
            width: "150px",
            Cell: ({ value }) => value || "-",
        },
        {
            accessor: "department_name",
            Header: "Department",
            sortable: true,
            filterable: true,
        },
        {
            accessor: "weekoff_rules",
            Header: "Week Off Rules",
            sortable: false,
            filterable: true,
            width: "200px",
            Cell: ({ value }) => value ? truncateText(value, 30, "300px", "balance") : "-"
        },
        {
            accessor: "notes",
            Header: "Notes",
            width: "150px",
            Cell: ({ value }) => value ? truncateText(value, 20, "250px", "balance") : "-"
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: true,
            filterable: true,
            width: "100px",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission
            ? [
                {
                    accessor: "action",
                    Header: "Actions",
                    sortable: false,
                    width: "80px",
                    Cell: ({ row }) => {
                        return (
                            <div className="flex items-center justify-center gap-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                    onClick={() => handleEditWeekOff(row)}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        );
                    },
                },
            ]
            : []),
    ];

    // UI
    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Calendar}
                title="Employee Week Off"
                subtitle={`Manage week-offs for ${configBranchLabel()}. Changes here do not affect other branches.`}
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={handleAddWeekOff}
                buttonName="Add Week Off"
            />

            <Reactable
                columns={EmployeeWeekOffColumns}
                data={EmployeeWeekOffData}
                setData={setEmployeeWeekOffData}
                setTotalCount={setTotalRecords}
                url={EMPLOYEE_WEEK_OFF}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isDraftRequired={!!draftPermission}
                isCSVRequired={true}
                isPDFRequired={true}
                isDateRequired={false}
            />
        </ContainerCard>
    );
};

export default EmployeeWeekOff;